import {
  ChartSubtype,
  ChartType
} from "components/DataExplorationV2/ChartSubtypes";
import { Tag } from "interfaces/tag";
import { DataExplorationSeries } from "reducers/configurableChartReducer";
import {
  LineSeries,
  RangeSeries,
  ScadaMedianDataSource,
  ScadaRangeDataSource
} from "interfaces/chart";

export const DEFAULT_LINE_SUBTYPE = ChartSubtype.LINE_WITH_MARKERS;

export const enum MenuType {
  CHART_TYPE = "chart-type",
  CONFIGURE = "configure",
  EDIT = "edit",
  NONE = "none"
}

export interface MenuState {
  selectedMenu: MenuType;
  chartType: ChartType;
  chartSubtype: ChartSubtype;
  selectedSeriesId: string | null;
  selectedYAxisId: string | null;
  selectedReferenceLineId: string | null;
}

//region actions
type MenuAction =
  | ChangeMenuAction
  | ChangeChartTypeAction
  | ChangeChartSubTypeAction
  | ChangeSelectedSeriesAction
  | ChangeSelectedYAxisIdAction
  | ChangeSelectedReferenceLineIdAction;

type ChangeMenuAction = {
  type: "changeMenu";
  selectedMenu: MenuType;
};

type ChangeChartTypeAction = {
  type: "changeChartType";
  chartType: ChartType;
};

type ChangeChartSubTypeAction = {
  type: "changeChartSubtype";
  chartSubtype: ChartSubtype;
};

type ChangeSelectedSeriesAction = {
  type: "changeSelectedSeries";
  seriesId: string | null;
};

type ChangeSelectedYAxisIdAction = {
  type: "changeSelectedYAxisId";
  selectedYAxisId: string | null;
};

type ChangeSelectedReferenceLineIdAction = {
  type: "changeSelectedReferenceLineId";
  selectedReferenceLineId: string | null;
};
//endregion

//region action functions
const changeMenu = (
  prevState: MenuState,
  action: ChangeMenuAction
): MenuState => {
  const selectedMenu = action.selectedMenu;

  if (prevState.selectedMenu !== selectedMenu) {
    return { ...prevState, selectedMenu: selectedMenu };
  } else {
    return { ...prevState, selectedMenu: MenuType.NONE };
  }
};

const changeChartType = (
  prevState: MenuState,
  action: ChangeChartTypeAction
): MenuState => {
  switch (action.chartType) {
    case "line":
      return {
        ...prevState,
        chartType: ChartType.LINE,
        chartSubtype: DEFAULT_LINE_SUBTYPE
      };
    default:
      console.error("unhandled chart type");
      return prevState;
  }
};

const changeChartSubtype = (
  prevState: MenuState,
  action: ChangeChartSubTypeAction
): MenuState => {
  return { ...prevState, chartSubtype: action.chartSubtype };
};

const changeSelectedSeries = (
  prevState: MenuState,
  action: ChangeSelectedSeriesAction
): MenuState => {
  return { ...prevState, selectedSeriesId: action.seriesId };
};

const changeSelectedYAxisId = (
  prevState: MenuState,
  action: ChangeSelectedYAxisIdAction
): MenuState => {
  return { ...prevState, selectedYAxisId: action.selectedYAxisId };
};

const changeSelectedReferenceLineId = (
  prevState: MenuState,
  action: ChangeSelectedReferenceLineIdAction
): MenuState => {
  return {
    ...prevState,
    selectedReferenceLineId: action.selectedReferenceLineId
  };
};
//endregion

export const menuReducer = (
  state: MenuState,
  action: MenuAction
): MenuState => {
  switch (action.type) {
    case "changeMenu":
      return changeMenu(state, action);
    case "changeChartType":
      return changeChartType(state, action);
    case "changeChartSubtype":
      return changeChartSubtype(state, action);
    case "changeSelectedSeries":
      return changeSelectedSeries(state, action);
    case "changeSelectedYAxisId":
      return changeSelectedYAxisId(state, action);
    case "changeSelectedReferenceLineId":
      return changeSelectedReferenceLineId(state, action);
    default:
      throw new Error("Unhandled action in chart menu reducer");
  }
};

//region chart menu helper functions TODO: move these into separate file
export const isSiteAverageSeries = (
  series: DataExplorationSeries
): series is LineSeries<ScadaMedianDataSource> => {
  return series.dataSource.type === "scada-median";
};

export const isSiteRangeSeries = (
  series: DataExplorationSeries
): series is RangeSeries<ScadaRangeDataSource> => {
  return series.dataSource.type === "scada-range";
};

export const isLinkedSiteAverage = (
  series: DataExplorationSeries,
  parentId: string | null
): boolean => {
  return (
    parentId !== null &&
    series.parentId === parentId &&
    isSiteAverageSeries(series)
  );
};

export const isLinkedSiteRange = (
  series: DataExplorationSeries,
  parentId: string | null
): boolean => {
  return (
    parentId !== null &&
    series.parentId === parentId &&
    isSiteRangeSeries(series)
  );
};

export const getSiteAverageTitle = (tag: Tag): string =>
  `${tag.name} - Site Average`;

export const getSiteRangeTitle = (tag: Tag): string =>
  `${tag.name} - Site Range`;

export const getSeriesTitle = (
  tagName?: string | null,
  machineName?: string | null
): string => {
  return `${tagName ?? "Unset"} - ${machineName ?? "Unset"}`;
};
//endregion
